import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useMasterEvent } from '../../../context/MasterEventContext';
import { authService } from '../../../services/auth';
import { openBetsService } from '../../../services/openBets';
import Avatar from '../../shared/Avatar';
import DateTimePicker from '../../shared/DateTimePicker';

const CURRENCY_SYMBOLS = { NOK: 'kr', USD: '$', EUR: '€' };

const s = {
  wrap: { padding: 16, display: 'flex', flexDirection: 'column', gap: 16 },
  header: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  icon: { fontSize: 26 },
  title: { fontSize: 18, fontWeight: 700, margin: 0 },
  desc: { fontSize: 13, color: 'rgba(255,255,255,0.55)', margin: '2px 0 0' },
  card: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 14 },
  cardDraft: { borderStyle: 'dashed', borderColor: 'rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.04)' },
  cardSettled: { borderColor: 'rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.04)' },
  row: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  input: { padding: '8px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '8px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' },
  select: { padding: '8px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', cursor: 'pointer' },
  label: { fontSize: 11, color: 'rgba(255,255,255,0.45)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 3 },
  btnPrimary: { padding: '9px 16px', background: '#22c55e', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  btnSecondary: { padding: '8px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'rgba(255,255,255,0.7)', fontSize: 12, cursor: 'pointer' },
  btnDanger: { padding: '7px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: '#f87171', fontSize: 12, cursor: 'pointer' },
  btnGhost: { padding: '6px 10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: 'rgba(255,255,255,0.6)', fontSize: 11, cursor: 'pointer' },
  pillFor: { padding: '3px 10px', borderRadius: 10, background: 'rgba(34,197,94,0.15)', color: '#22c55e', fontSize: 11, fontWeight: 700, letterSpacing: 0.3 },
  pillAgainst: { padding: '3px 10px', borderRadius: 10, background: 'rgba(239,68,68,0.15)', color: '#f87171', fontSize: 11, fontWeight: 700, letterSpacing: 0.3 },
  badge: (bg, color) => ({ padding: '3px 10px', borderRadius: 10, background: bg, color, fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }),
  participant: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 8, fontSize: 13 },
  totals: { display: 'flex', gap: 14, marginTop: 8, padding: '10px 12px', background: 'rgba(255,255,255,0.04)', borderRadius: 8 },
  totalBlock: { flex: 1 },
  totalLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'rgba(255,255,255,0.4)' },
  totalValue: { fontSize: 18, fontWeight: 800, marginTop: 2 },
  comment: { display: 'flex', gap: 8, padding: '6px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, fontSize: 12, color: 'rgba(255,255,255,0.7)' },
  empty: { padding: 16, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 13 },
  error: { padding: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: '#f87171', fontSize: 12 },
};

function formatMoney(amount, currency) {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  const n = Number(amount) || 0;
  return `${n.toLocaleString('en-GB', { maximumFractionDigits: 2 })} ${sym}`;
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return null; }
}

function SideBadge({ side }) {
  return side === 'against' ? <span style={s.pillAgainst}>AGAINST</span> : <span style={s.pillFor}>FOR</span>;
}

function StatusBadge({ status, settled }) {
  if (settled || status === 'settled') return <span style={s.badge('rgba(34,197,94,0.18)', '#22c55e')}>SETTLED</span>;
  if (status === 'draft') return <span style={s.badge('rgba(245,158,11,0.18)', '#fbbf24')}>DRAFT</span>;
  return <span style={s.badge('rgba(59,130,246,0.18)', '#60a5fa')}>LIVE</span>;
}

function CreateBetForm({ onSubmit, onCancel }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState('NOK');
  const [amount, setAmount] = useState('');
  const [side, setSide] = useState('for');
  const [conclusionDate, setConclusionDate] = useState('');
  const [publishImmediately, setPublishImmediately] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) { setError('Title is required'); return; }
    const num = Number(amount);
    if (!num || num <= 0) { setError('Enter a positive amount'); return; }
    onSubmit({
      title: title.trim(),
      description: description.trim(),
      currency,
      creatorAmount: num,
      creatorSide: side,
      conclusionDate: conclusionDate || null,
      status: publishImmediately ? 'live' : 'draft',
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ ...s.card, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>New Open Bet</h4>
      <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="What's the bet? e.g. 'Norway beats Sweden tonight'"
        style={{ ...s.input, width: '100%' }} autoFocus />
      <textarea value={description} onChange={(e) => setDescription(e.target.value)}
        placeholder="Extra details (optional) — rules, terms, who decides..."
        rows={2} style={s.textarea} />

      <div style={s.row}>
        <div style={{ flex: 1, minWidth: 110 }}>
          <span style={s.label}>Your Side</span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" onClick={() => setSide('for')}
              style={{ ...s.input, padding: '8px 10px', flex: 1, cursor: 'pointer',
                background: side === 'for' ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.06)',
                color: side === 'for' ? '#22c55e' : 'rgba(255,255,255,0.7)',
                borderColor: side === 'for' ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.1)',
                fontWeight: 700 }}>FOR</button>
            <button type="button" onClick={() => setSide('against')}
              style={{ ...s.input, padding: '8px 10px', flex: 1, cursor: 'pointer',
                background: side === 'against' ? 'rgba(239,68,68,0.18)' : 'rgba(255,255,255,0.06)',
                color: side === 'against' ? '#f87171' : 'rgba(255,255,255,0.7)',
                borderColor: side === 'against' ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)',
                fontWeight: 700 }}>AGAINST</button>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 90 }}>
          <span style={s.label}>Currency</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ ...s.select, width: '100%' }}>
            {openBetsService.CURRENCIES.map((c) => (
              <option key={c} value={c}>{c} {CURRENCY_SYMBOLS[c]}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <span style={s.label}>Your Stake</span>
          <input type="number" min="0" step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)} placeholder="100"
            style={{ ...s.input, width: '100%' }} />
        </div>
      </div>

      <div>
        <span style={s.label}>Conclusion Date (optional)</span>
        <DateTimePicker value={conclusionDate} onChange={setConclusionDate} placeholder="When does this bet conclude?" />
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
          <input type="checkbox" checked={publishImmediately} onChange={(e) => setPublishImmediately(e.target.checked)} />
          Publish immediately (otherwise saves as draft)
        </label>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={onCancel} style={s.btnSecondary}>Cancel</button>
          <button type="submit" style={s.btnPrimary}>{publishImmediately ? 'Publish Bet' : 'Save Draft'}</button>
        </div>
      </div>
    </form>
  );
}

function JoinBetForm({ bet, onJoin, onCancel, currentEntry }) {
  const [amount, setAmount] = useState(currentEntry?.amount?.toString() || '');
  const [side, setSide] = useState(currentEntry?.side || (bet.creatorSide === 'for' ? 'against' : 'for'));
  const [note, setNote] = useState(currentEntry?.note || '');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const num = Number(amount);
    if (!num || num <= 0) { setError('Enter a positive amount'); return; }
    try {
      onJoin({ amount: num, side, note });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ ...s.card, display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
      <h5 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{currentEntry ? 'Update your stake' : 'Join this bet'}</h5>
      <div style={s.row}>
        <div style={{ flex: 1, minWidth: 110 }}>
          <span style={s.label}>Your Side</span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" onClick={() => setSide('for')}
              style={{ ...s.input, padding: '8px 10px', flex: 1, cursor: 'pointer',
                background: side === 'for' ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.06)',
                color: side === 'for' ? '#22c55e' : 'rgba(255,255,255,0.7)',
                borderColor: side === 'for' ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.1)',
                fontWeight: 700 }}>FOR</button>
            <button type="button" onClick={() => setSide('against')}
              style={{ ...s.input, padding: '8px 10px', flex: 1, cursor: 'pointer',
                background: side === 'against' ? 'rgba(239,68,68,0.18)' : 'rgba(255,255,255,0.06)',
                color: side === 'against' ? '#f87171' : 'rgba(255,255,255,0.7)',
                borderColor: side === 'against' ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)',
                fontWeight: 700 }}>AGAINST</button>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <span style={s.label}>Stake ({CURRENCY_SYMBOLS[bet.currency]})</span>
          <input type="number" min="0" step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)} placeholder="100"
            style={{ ...s.input, width: '100%' }} autoFocus />
        </div>
      </div>
      <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note (e.g. trash talk)"
        style={{ ...s.input, width: '100%' }} />
      {error && <div style={s.error}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <button type="button" onClick={onCancel} style={s.btnSecondary}>Cancel</button>
        <button type="submit" style={s.btnPrimary}>{currentEntry ? 'Update Stake' : 'Join Bet'}</button>
      </div>
    </form>
  );
}

function SettleForm({ bet, onSettle, onCancel }) {
  const [winningSide, setWinningSide] = useState('for');
  const [outcome, setOutcome] = useState('');

  return (
    <div style={{ ...s.card, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h5 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>Settle this bet</h5>
      <div>
        <span style={s.label}>Winning Side</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {['for', 'against', 'void'].map((opt) => (
            <button key={opt} type="button" onClick={() => setWinningSide(opt)}
              style={{ ...s.input, padding: '8px 10px', flex: 1, cursor: 'pointer',
                background: winningSide === opt ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.06)',
                color: winningSide === opt ? '#60a5fa' : 'rgba(255,255,255,0.7)',
                borderColor: winningSide === opt ? 'rgba(59,130,246,0.4)' : 'rgba(255,255,255,0.1)',
                fontWeight: 700, textTransform: 'uppercase' }}>{opt}</button>
          ))}
        </div>
      </div>
      <input type="text" value={outcome} onChange={(e) => setOutcome(e.target.value)}
        placeholder="What happened? (optional)"
        style={{ ...s.input, width: '100%' }} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <button type="button" onClick={onCancel} style={s.btnSecondary}>Cancel</button>
        <button type="button" onClick={() => onSettle({ winningSide, outcome })} style={s.btnPrimary}>Mark Settled</button>
      </div>
    </div>
  );
}

function BetCard({ bet, currentUserId, isEventAdmin, allUsers, onUpdate }) {
  const [showJoin, setShowJoin] = useState(false);
  const [showSettle, setShowSettle] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [error, setError] = useState('');

  const totals = useMemo(() => openBetsService.computeTotals(bet), [bet]);
  const userOf = (uid) => allUsers.find((u) => u.id === uid || u.cognitoId === uid);
  const creator = userOf(bet.createdBy);
  const isCreator = bet.createdBy === currentUserId;
  const canManage = isCreator || isEventAdmin;
  const currentEntry = bet.participants.find((p) => p.userId === currentUserId);

  const cardStyle = {
    ...s.card,
    ...(bet.status === 'draft' ? s.cardDraft : {}),
    ...(bet.settled ? s.cardSettled : {}),
  };

  const handleJoin = ({ amount, side, note }) => {
    try {
      const updated = openBetsService.joinBet(bet.id, { userId: currentUserId, amount, side, note });
      onUpdate(updated);
      setShowJoin(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLeave = () => {
    if (!window.confirm('Remove your stake from this bet?')) return;
    try {
      const updated = openBetsService.leaveBet(bet.id, currentUserId);
      onUpdate(updated);
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePublish = () => onUpdate(openBetsService.publish(bet.id));
  const handleMoveToDraft = () => onUpdate(openBetsService.moveToDraft(bet.id));
  const handleDelete = () => {
    if (!window.confirm('Delete this bet? This cannot be undone.')) return;
    openBetsService.delete(bet.id);
    onUpdate(null);
  };

  const handleSettle = ({ winningSide, outcome }) => {
    const updated = openBetsService.settle(bet.id, { winningSide, outcome });
    onUpdate(updated);
    setShowSettle(false);
  };

  const handleUnsettle = () => {
    if (!window.confirm('Reopen this bet?')) return;
    onUpdate(openBetsService.unsettle(bet.id));
  };

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    try {
      const updated = openBetsService.addComment(bet.id, { userId: currentUserId, text: commentText });
      setCommentText('');
      onUpdate(updated);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteComment = (commentId) => {
    const updated = openBetsService.deleteComment(bet.id, commentId);
    onUpdate(updated);
  };

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
        <Avatar user={creator} size={28} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14 }}>{creator?.displayName || 'Unknown'}</strong>
            <StatusBadge status={bet.status} settled={bet.settled} />
            <SideBadge side={bet.creatorSide} />
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{formatDate(bet.createdAt)}</span>
          </div>
          <h4 style={{ margin: '6px 0 2px', fontSize: 15, fontWeight: 700 }}>{bet.title}</h4>
          {bet.description && <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>{bet.description}</p>}
          {bet.conclusionDate && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
              ⏱️ Concludes: {formatDate(bet.conclusionDate)}
            </div>
          )}
        </div>
      </div>

      {/* Totals */}
      <div style={s.totals}>
        <div style={s.totalBlock}>
          <div style={s.totalLabel}>For pool</div>
          <div style={{ ...s.totalValue, color: '#22c55e' }}>{formatMoney(totals.for, bet.currency)}</div>
        </div>
        <div style={s.totalBlock}>
          <div style={s.totalLabel}>Against pool</div>
          <div style={{ ...s.totalValue, color: '#f87171' }}>{formatMoney(totals.against, bet.currency)}</div>
        </div>
        <div style={s.totalBlock}>
          <div style={s.totalLabel}>Players</div>
          <div style={s.totalValue}>{1 + bet.participants.length}</div>
        </div>
      </div>

      {/* Participants list (creator + joiners) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
        <div style={s.participant}>
          <Avatar user={creator} size={20} />
          <strong style={{ fontSize: 13 }}>{creator?.displayName || 'Unknown'}</strong>
          <span style={{ marginLeft: 'auto', fontWeight: 700, color: bet.creatorSide === 'for' ? '#22c55e' : '#f87171' }}>
            {formatMoney(bet.creatorAmount, bet.currency)}
          </span>
          <SideBadge side={bet.creatorSide} />
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>(creator)</span>
        </div>
        {bet.participants.map((p) => {
          const u = userOf(p.userId);
          return (
            <div key={p.userId} style={s.participant}>
              <Avatar user={u} size={20} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <strong style={{ fontSize: 13 }}>{u?.displayName || 'Unknown'}</strong>
                  {p.note && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>"{p.note}"</span>}
                </div>
              </div>
              <span style={{ fontWeight: 700, color: p.side === 'for' ? '#22c55e' : '#f87171' }}>
                {formatMoney(p.amount, bet.currency)}
              </span>
              <SideBadge side={p.side} />
            </div>
          );
        })}
      </div>

      {/* Settled state — show outcome */}
      {bet.settled && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(34,197,94,0.1)', borderRadius: 8, fontSize: 13 }}>
          <strong style={{ color: '#22c55e' }}>Result:</strong>{' '}
          {bet.winningSide === 'void' ? 'Voided — stakes returned' : (
            <>
              <SideBadge side={bet.winningSide} /> won
            </>
          )}
          {bet.outcome && <div style={{ marginTop: 4, color: 'rgba(255,255,255,0.7)' }}>{bet.outcome}</div>}
          {bet.settledAt && <div style={{ marginTop: 4, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Settled {formatDate(bet.settledAt)}</div>}
        </div>
      )}

      {error && <div style={{ ...s.error, marginTop: 8 }}>{error}</div>}

      {/* Action bar */}
      <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
        {/* Join / leave (only if bet is live and not settled) */}
        {bet.status === 'live' && !bet.settled && !isCreator && (
          <>
            <button onClick={() => setShowJoin(!showJoin)} style={s.btnPrimary}>
              {currentEntry ? '✏️ Update Stake' : '➕ Join Bet'}
            </button>
            {currentEntry && (
              <button onClick={handleLeave} style={s.btnDanger}>Remove Stake</button>
            )}
          </>
        )}

        {/* Draft / publish toggle */}
        {canManage && !bet.settled && (
          bet.status === 'draft'
            ? <button onClick={handlePublish} style={s.btnSecondary}>🚀 Publish (go live)</button>
            : <button onClick={handleMoveToDraft} style={s.btnGhost}>📝 Move to draft</button>
        )}

        {/* Settle / unsettle */}
        {canManage && bet.status === 'live' && !bet.settled && (
          <button onClick={() => setShowSettle(!showSettle)} style={s.btnSecondary}>✅ Settle Bet</button>
        )}
        {canManage && bet.settled && (
          <button onClick={handleUnsettle} style={s.btnGhost}>↩️ Reopen</button>
        )}

        {canManage && (
          <button onClick={handleDelete} style={s.btnDanger}>🗑️ Delete</button>
        )}
      </div>

      {showJoin && (
        <JoinBetForm bet={bet} currentEntry={currentEntry}
          onJoin={handleJoin} onCancel={() => setShowJoin(false)} />
      )}

      {showSettle && (
        <SettleForm bet={bet} onSettle={handleSettle} onCancel={() => setShowSettle(false)} />
      )}

      {/* Comments */}
      <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
          Comments ({(bet.comments || []).length})
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto', marginBottom: 6 }}>
          {(bet.comments || []).length === 0 ? (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>No comments yet.</div>
          ) : (
            bet.comments.map((c) => {
              const cu = userOf(c.userId);
              const canDelete = c.userId === currentUserId || canManage;
              return (
                <div key={c.id} style={s.comment}>
                  <Avatar user={cu} size={16} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ color: '#fff', fontSize: 12 }}>{cu?.displayName || 'Unknown'}</strong>{' '}
                    {c.text}
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{formatDate(c.createdAt)}</div>
                  </div>
                  {canDelete && (
                    <button onClick={() => handleDeleteComment(c.id)}
                      style={{ background: 'none', border: 'none', color: 'rgba(239,68,68,0.6)', cursor: 'pointer', fontSize: 11, padding: 2 }}
                      title="Delete comment">✕</button>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="text" value={commentText} onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
            placeholder="Add a comment..."
            style={{ ...s.input, flex: 1 }} />
          <button onClick={handleAddComment} disabled={!commentText.trim()}
            style={{ ...s.btnPrimary, opacity: commentText.trim() ? 1 : 0.5 }}>Post</button>
        </div>
      </div>
    </div>
  );
}

export default function OpenBetsActivity({ activity }) {
  const { user } = useAuth();
  const { isAdmin, isEventAdmin, myMembership } = useMasterEvent();
  const isMember = !!myMembership;
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('all'); // all | live | draft | settled
  const allUsers = useMemo(() => authService.getAllUsers(), []);

  const loadBets = useCallback(async () => {
    try {
      const data = await openBetsService.fetchForActivity(activity.id);
      setBets(data);
    } catch (err) {
      console.warn('[OpenBets] load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [activity.id]);

  useEffect(() => { loadBets(); }, [loadBets]);

  // Poll every 4s so participants see new bets/joiners/comments live
  useEffect(() => {
    const interval = setInterval(loadBets, 4000);
    return () => clearInterval(interval);
  }, [loadBets]);

  const handleCreate = (data) => {
    openBetsService.create({
      activityId: activity.id,
      masterEventId: activity.masterEventId,
      createdBy: user.id,
      ...data,
    });
    setShowCreate(false);
    loadBets();
  };

  const handleBetUpdate = (updated) => {
    if (updated === null) {
      // Deleted
      loadBets();
      return;
    }
    setBets((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  };

  const filtered = useMemo(() => {
    if (filter === 'all') return bets;
    if (filter === 'settled') return bets.filter((b) => b.settled);
    if (filter === 'live') return bets.filter((b) => b.status === 'live' && !b.settled);
    if (filter === 'draft') return bets.filter((b) => b.status === 'draft');
    return bets;
  }, [bets, filter]);

  const counts = useMemo(() => ({
    all: bets.length,
    live: bets.filter((b) => b.status === 'live' && !b.settled).length,
    draft: bets.filter((b) => b.status === 'draft').length,
    settled: bets.filter((b) => b.settled).length,
  }), [bets]);

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <span style={s.icon}>🤝</span>
        <div style={{ flex: 1 }}>
          <h3 style={s.title}>{activity.name}</h3>
          {activity.description && <p style={s.desc}>{activity.description}</p>}
        </div>
        {isMember && (
          <button onClick={() => setShowCreate(!showCreate)} style={s.btnPrimary}>
            {showCreate ? '✕ Cancel' : '+ New Bet'}
          </button>
        )}
      </div>

      {showCreate && <CreateBetForm onSubmit={handleCreate} onCancel={() => setShowCreate(false)} />}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: 'All' },
          { id: 'live', label: 'Live' },
          { id: 'draft', label: 'Drafts' },
          { id: 'settled', label: 'Settled' },
        ].map((tab) => (
          <button key={tab.id} onClick={() => setFilter(tab.id)}
            style={{ ...s.btnGhost, padding: '6px 12px',
              background: filter === tab.id ? 'rgba(59,130,246,0.15)' : 'transparent',
              color: filter === tab.id ? '#60a5fa' : 'rgba(255,255,255,0.6)',
              borderColor: filter === tab.id ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.1)' }}>
            {tab.label} <span style={{ opacity: 0.6 }}>({counts[tab.id]})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={s.empty}>Loading bets...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>
          {bets.length === 0
            ? 'No bets yet. Be the first — create one above!'
            : `No ${filter} bets.`}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map((bet) => (
            <BetCard key={bet.id} bet={bet}
              currentUserId={user?.id}
              isEventAdmin={isEventAdmin || isAdmin}
              allUsers={allUsers}
              onUpdate={handleBetUpdate} />
          ))}
        </div>
      )}
    </div>
  );
}
